import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { SectionHead } from '@/components/layout';
import { Button, Container, Pill, Text } from '@/components/ui';
import { SeoHead } from '@/components/SeoHead';
import {
  useDanceStyles,
  useFirstClassTips,
  useGlossar,
  type DanceStyle,
  type FirstClassTip,
  type GlossarItem,
} from '@/content/info';
import { useHover } from '@/lib/useHover';
import { MoodSection, fluidFontSize, fluidLineHeight, palette, useTheme } from '@/theme';

const webTransition = (props = 'background-color, color, transform, border-color', dur = '180ms') =>
  Platform.OS === 'web' ? ({ transitionProperty: props, transitionDuration: dur } as any) : null;

export default function InfoScreen() {
  const theme = useTheme();
  const { t } = useTranslation();
  const danceStyles = useDanceStyles();
  const firstClassTips = useFirstClassTips();
  const glossar = useGlossar();

  return (
    <View>
      <SeoHead
        title={t('info.seo.title')}
        description={t('info.seo.description')}
      />

      {/* Pagehead */}
      <View style={{ paddingTop: 48, paddingBottom: 48 }}>
        <Container>
          <View style={st.eyebrowRow}>
            <View style={[st.dot, { backgroundColor: theme.colors.accentAlt }]} />
            <Text variant="meta" tone="fgMute">{t('info.hero.eyebrow')}</Text>
          </View>
          <Text
            style={{
              fontFamily: theme.fontFamily.display,
              fontSize: fluidFontSize(48, 11.5, 96) as any,
              lineHeight: fluidLineHeight(0.95, 92) as any,
              color: theme.colors.fg,
              letterSpacing: -2,
            }}
          >
            {t('info.hero.titleLine1')}{'\n'}
            <Text
              italic
              style={{
                fontFamily: theme.fontFamily.displayItalic,
                fontStyle: 'italic',
                color: theme.colors.accent,
                fontSize: fluidFontSize(48, 11.5, 96) as any,
              }}
            >
              {t('info.hero.titleLine2')}
            </Text>
          </Text>
          <Text variant="bodyLarge" tone="fgSoft" style={{ marginTop: 24, maxWidth: 680 }}>
            {t('info.hero.intro')}
          </Text>
        </Container>
      </View>

      {danceStyles.map((style, i) => {
        const num = `0${i + 2}`;
        return style.midnight ? (
          <MoodSection
            key={style.id}
            mood="midnight"
            style={[
              st.midnight,
              Platform.OS === 'web'
                ? ({ boxShadow: `0 0 80px -20px ${palette.wine}55, 0 24px 60px -24px #15082E80` } as any)
                : null,
            ]}
          >
            <Container>
              <SectionHead
                idx={`${num} / ${style.name}`}
                title={style.kicker.split(' ').slice(0, 3).join(' ')}
                italic={style.kicker.split(' ').slice(3).join(' ') || ' '}
                sub={style.history}
                invert
              />
              <StyleBody style={style} invert />
            </Container>
          </MoodSection>
        ) : (
          <View key={style.id} style={{ paddingVertical: 96 }}>
            <Container>
              <SectionHead
                idx={`${num} / ${style.name}`}
                title={style.kicker.split(' ').slice(0, 3).join(' ')}
                italic={style.kicker.split(' ').slice(3).join(' ') || ' '}
                sub={style.history}
              />
              <StyleBody style={style} />
            </Container>
          </View>
        );
      })}

      {/* 05 Floor-Etikette */}
      <View style={{ paddingVertical: 96 }}>
        <Container>
          <SectionHead
            idx={t('info.etikette.idx')}
            title={t('info.etikette.title')}
            italic={t('info.etikette.italic')}
            sub={t('info.etikette.sub')}
          />
          <View style={st.tipsGrid}>
            {firstClassTips.map((tip) => (
              <TipCard key={tip.n} tip={tip} />
            ))}
          </View>
        </Container>
      </View>

      {/* 06 Glossar */}
      <View style={{ paddingVertical: 64 }}>
        <Container>
          <SectionHead
            idx={t('info.glossar.idx')}
            title={t('info.glossar.title')}
            italic={t('info.glossar.italic')}
            sub={t('info.glossar.sub')}
          />
          <View style={st.glossGrid}>
            {glossar.map((g) => (
              <GlossCard key={g.term} g={g} />
            ))}
          </View>
        </Container>
      </View>

      {/* CTA */}
      <View style={[st.ctaSection, { backgroundColor: theme.colors.bgSoft }]}>
        <Container>
          <View style={st.ctaRow}>
            <View style={{ flex: 1.4, minWidth: 320 }}>
              <Text
                style={{
                  fontFamily: theme.fontFamily.display,
                  fontSize: 56,
                  lineHeight: 56,
                  color: theme.colors.fg,
                }}
              >
                {t('info.cta.titleLine1')}{' '}
                <Text
                  italic
                  style={{
                    fontFamily: theme.fontFamily.displayItalic,
                    fontStyle: 'italic',
                    color: theme.colors.accent,
                    fontSize: 56,
                  }}
                >
                  {t('info.cta.titleLine2')}
                </Text>
              </Text>
              <Text variant="bodyLarge" tone="fgSoft" style={{ marginTop: 16, maxWidth: 520 }}>
                {t('info.cta.body')}
              </Text>
            </View>
            <View style={st.ctaButtons}>
              <Button variant="primary" size="lg" trailingArrow href="/kurse">{t('info.cta.ctaPrimary')}</Button>
              <Button variant="ghost" size="lg" href="/kontakt">{t('info.cta.ctaSecondary')}</Button>
            </View>
          </View>
        </Container>
      </View>
    </View>
  );
}

function StyleBody({ style, invert }: { style: DanceStyle; invert?: boolean }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const onBgSoft = invert ? 'rgba(245,240,255,0.78)' : theme.colors.fgSoft;
  const accent = invert ? palette.ember : theme.colors.accent;

  return (
    <View style={st.styleBody}>
      <View style={st.styleLeft}>
        <Text
          variant="eyebrow"
          style={{ color: accent, marginTop: 8 }}
        >
          {t('info.styleBody.signatureMovesLabel')}
        </Text>
        <View style={st.moveRow}>
          {style.signature.map((m) => (
            <Pill key={m} variant={invert ? 'invert' : 'ghost'}>{m}</Pill>
          ))}
        </View>
      </View>
      <View
        style={[
          st.steck,
          {
            backgroundColor: invert ? 'rgba(245,240,255,0.06)' : theme.colors.bgSoft,
            borderColor: invert ? 'rgba(245,240,255,0.16)' : theme.colors.rule,
            borderRadius: 12,
          },
        ]}
      >
        <Text variant="eyebrow" style={{ color: accent }}>{t('info.styleBody.steckbriefLabel')}</Text>
        {style.steckbrief.map((s, i) => (
          <View
            key={s.label}
            style={[
              st.steckRow,
              {
                borderTopColor: invert ? 'rgba(245,240,255,0.16)' : theme.colors.rule,
                borderTopWidth: i === 0 ? 0 : 1,
              },
            ]}
          >
            <Text variant="meta" style={{ color: onBgSoft, flexBasis: 100 }}>{s.label}</Text>
            <Text
              style={{ color: invert ? palette.paper : theme.colors.fg, fontSize: 15, lineHeight: 22, flex: 1 }}
            >
              {s.value}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}

function TipCard({ tip }: { tip: FirstClassTip }) {
  const theme = useTheme();
  const { hovered, hoverProps } = useHover();
  return (
    <Pressable
      {...(hoverProps as any)}
      style={[
        st.tipCard,
        {
          backgroundColor: theme.colors.bgSoft,
          borderColor: hovered ? theme.colors.accent : theme.colors.rule,
          borderRadius: 12,
        },
        webTransition(),
        hovered ? { transform: [{ translateY: -3 }] } : null,
      ]}
    >
      <Text variant="meta" style={{ color: theme.colors.accent }}>{tip.n}</Text>
      <Text variant="h4" style={{ marginTop: 8 }}>{tip.title}</Text>
      <Text variant="body" tone="fgSoft" style={{ marginTop: 8 }}>{tip.body}</Text>
    </Pressable>
  );
}

function GlossCard({ g }: { g: GlossarItem }) {
  const theme = useTheme();
  const { hovered, hoverProps } = useHover();
  return (
    <Pressable
      {...(hoverProps as any)}
      style={[
        st.glossCard,
        {
          backgroundColor: theme.colors.bg,
          borderColor: hovered ? theme.colors.accent : theme.colors.rule,
          borderRadius: 12,
        },
        webTransition(),
        hovered ? { transform: [{ translateY: -2 }] } : null,
      ]}
    >
      <View style={st.glossHead}>
        <Text
          italic
          style={{
            fontFamily: theme.fontFamily.displayItalic,
            fontStyle: 'italic',
            fontSize: 22,
            color: theme.colors.accent,
          }}
        >
          {g.term}
        </Text>
        <Pill variant="ghost">{g.lang}</Pill>
      </View>
      <Text variant="body" tone="fgSoft" style={{ marginTop: 8 }}>{g.def}</Text>
    </Pressable>
  );
}

const st = StyleSheet.create({
  eyebrowRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 32 },
  dot: { width: 6, height: 6, borderRadius: 3 },
  midnight: {
    paddingVertical: 96,
    paddingHorizontal: 8,
    marginHorizontal: 24,
    marginVertical: 48,
    borderRadius: 12,
    overflow: 'hidden',
  },
  styleBody: { flexDirection: 'row', flexWrap: 'wrap', gap: 32, alignItems: 'flex-start' },
  styleLeft: { flex: 1, minWidth: 320 },
  moveRow: { marginTop: 12, flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  steck: { flexBasis: 320, flexGrow: 1, padding: 24, borderWidth: 1 },
  steckRow: { paddingVertical: 12, flexDirection: 'row', alignItems: 'flex-start', gap: 16 },
  tipsGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 16 },
  tipCard: { flex: 1, minWidth: 240, padding: 24, borderWidth: 1 },
  glossGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  glossCard: { flex: 1, minWidth: 220, padding: 20, borderWidth: 1 },
  glossHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  ctaSection: { paddingVertical: 96 },
  ctaRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 48, alignItems: 'center' },
  ctaButtons: { flex: 1, minWidth: 280, gap: 12 },
});
